// ===============================
// ВСПОМОГАТЕЛЬНЫЕ УТИЛИТЫ ДЛЯ ДАТ
// ===============================

const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date.getTime());
};

const safeCreateDate = (value) => {
  if (typeof value !== "string") return null;
  const d = new Date(value);
  return isValidDate(d) ? d : null;
};

/**
 * Любой вход (ISO, ISO с временем) → YYYY-MM-DD
 */
export const normalizeIncomingDate = (value) => {
  const d = safeCreateDate(value);
  if (!d) return null;
  return d.toISOString().slice(0, 10);
};

const normalizeDate = (date) => {
  if (!isValidDate(date)) return null;
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// ===============================
// ISO НЕДЕЛИ
// ===============================

const getISOWeek = (date) => {
  if (!isValidDate(date)) return 1;

  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7;

  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();

  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  }

  return 1 + Math.ceil((firstThursday - target) / 604800000);
};

// ===============================
// ФОРМАТИРОВАНИЕ ДАТ
// ===============================

const formatDate = (date, period) => {
  if (!isValidDate(date)) return null;

  const year = date.getFullYear();
  const month = date.getMonth();

  switch (period) {
    case "day":
      return date.toISOString().slice(0, 10);

    case "week": {
      const week = getISOWeek(date);
      return `${year}-W${String(week).padStart(2, "0")}`;
    }

    case "month":
      return `${year}-${String(month + 1).padStart(2, "0")}`;

    default:
      return date.toISOString().slice(0, 10);
  }
};

// ===============================
// ГЕНЕРАЦИЯ ДИАПАЗОНА ДАТ
// ===============================

export const generateDateRange = (
  startDate,
  endDate,
  period
) => {
  const start = safeCreateDate(startDate);
  const end = safeCreateDate(endDate);

  if (!start || !end) return [];

  const normalizedStart = normalizeDate(start);
  const normalizedEnd = normalizeDate(end);

  if (!normalizedStart || !normalizedEnd) return [];
  if (normalizedStart > normalizedEnd) return [];

  const dates = [];
  const current = new Date(normalizedStart);

  while (current <= normalizedEnd) {
    const formatted = formatDate(current, period);
    if (formatted && !dates.includes(formatted)) {
      dates.push(formatted);
    }

    if (period === "day") current.setDate(current.getDate() + 1);
    if (period === "week") current.setDate(current.getDate() + 7);
    if (period === "month") current.setMonth(current.getMonth() + 1);
  }

  return dates;
};

// ===============================
// ОСНОВНАЯ ФУНКЦИЯ
// ===============================

export const fillDataGaps = (
  chartData,
  startDate,
  endDate,
  aggregation
) => {
  if (!Array.isArray(chartData) || chartData.length === 0) {
    return chartData;
  }

  const period =
    aggregation === "1"
      ? "day"
      : aggregation === "7"
      ? "week"
      : "month";

  const normalizedStart = normalizeIncomingDate(startDate);
  const normalizedEnd = normalizeIncomingDate(endDate);

  if (!normalizedStart || !normalizedEnd) {
    console.error("fillDataGaps: invalid start/end", {
      startDate,
      endDate,
    });
    return chartData;
  }

  return chartData.map(({ marker, data, ...rest }) => {
    if (!Array.isArray(data)) {
      return { marker, data: [], ...rest };
    }

    // 1️⃣ нормализуем входные данные
    const normalizedData = data
      .map((item) => {
        const normalizedDate = normalizeIncomingDate(item?.date);
        if (!normalizedDate) return null;

        return {
          ...item,
          date: normalizedDate,
        };
      })
      .filter(Boolean);

    // 2️⃣ строим полный диапазон
    const fullRange = generateDateRange(
      normalizedStart,
      normalizedEnd,
      period
    );

    // 3️⃣ мапа существующих данных
    const dataMap = new Map();
    normalizedData.forEach((item) => {
      dataMap.set(item.date, item);
    });

    // 4️⃣ заполняем пропуски
    const filledData = fullRange.map((date) => {
      return (
        dataMap.get(date) || {
          date,
          value: 0,
          isGap: true,
        }
      );
    });

    return {
      marker,
      data: filledData,
      ...rest,
    };
  });
};

// ===============================
// ДЛИНА ДАННЫХ
// ===============================

export const getActualDataLength = (chartData) => {
  if (!Array.isArray(chartData) || chartData.length === 0) return 0;
  return chartData[0]?.data?.length || 0;
};
